import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useSTPSubmission, useSTPPayload, useSubmitSTPToATO } from '@/hooks/useSTP';
import { useToast } from '@/components/ui/Toast';
import { formatDate, formatCurrency } from '@/lib/utils';

export function STPSubmissionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: submission } = useSTPSubmission(id!);
  const { data: payload } = useSTPPayload(id!);
  const submitToATO = useSubmitSTPToATO();

  const issues = payload?.validation?.issues || [];
  const employees = payload?.employees || [];
  const totals = payload?.totals;

  const handleSubmit = async () => {
    try {
      const result = await submitToATO.mutateAsync(id!);
      toast({
        title: result.lodged ? 'Lodged with ATO' : 'Validated, not lodged',
        description: result.lodged ? undefined : result.message,
        variant: result.lodged ? 'success' : 'default',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Submit failed';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    }
  };

  if (!submission) {
    return <div className="p-6 text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`STP Submission — ${submission.submissionType?.replace(/_/g, ' ')}`}
        description={submission.payRun ? `Pay run paid ${formatDate(submission.payRun.paymentDate)}` : ''}
      >
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/payroll/stp')}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          {submission.status === 'READY' && (
            <Button onClick={handleSubmit} disabled={submitToATO.isPending}>
              <Send className="mr-2 h-4 w-4" /> Lodge with ATO
            </Button>
          )}
        </div>
      </PageHeader>

      {/* Validation */}
      {issues.length > 0 && (
        <Card className="card-elevated border-destructive/50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" /> Validation issues ({issues.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {issues.map((issue: any, i: number) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                {issue.severity === 'ERROR' ? (
                  <XCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                )}
                <div>
                  <span className="font-mono text-xs mr-2">{issue.code}</span>
                  {issue.message}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {issues.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-emerald-600">
          <CheckCircle className="h-4 w-4" /> No validation issues — ready to lodge.
        </div>
      )}

      {/* Totals */}
      {totals && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="card-elevated">
            <CardContent className="pt-4">
              <div className="text-xl font-semibold font-mono" data-numbers>{formatCurrency(totals.grossEarnings)}</div>
              <div className="text-xs text-muted-foreground">Gross earnings</div>
            </CardContent>
          </Card>
          <Card className="card-elevated">
            <CardContent className="pt-4">
              <div className="text-xl font-semibold font-mono" data-numbers>{formatCurrency(totals.taxWithheld)}</div>
              <div className="text-xs text-muted-foreground">Tax withheld</div>
            </CardContent>
          </Card>
          <Card className="card-elevated">
            <CardContent className="pt-4">
              <div className="text-xl font-semibold font-mono" data-numbers>{formatCurrency(totals.superContribution)}</div>
              <div className="text-xs text-muted-foreground">Super guarantee</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Employees */}
      <Card className="card-elevated">
        <div className="px-5 py-3 border-b font-semibold text-sm">Employees ({employees.length})</div>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2">Employee</th>
                <th className="px-4 py-2">TFN</th>
                <th className="px-4 py-2 text-right">Gross</th>
                <th className="px-4 py-2 text-right">Tax</th>
                <th className="px-4 py-2 text-right">Super</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((emp: any) => (
                <tr key={emp.employeeId} className="border-b last:border-0">
                  <td className="px-4 py-2">{emp.name}</td>
                  <td className="px-4 py-2">
                    {emp.tfnSupplied ? (
                      <Badge variant="success">Supplied</Badge>
                    ) : emp.exemptionCode ? (
                      <Badge variant="secondary">Exempt</Badge>
                    ) : (
                      <Badge variant="destructive">Missing</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-mono" data-numbers>{formatCurrency(emp.grossEarnings)}</td>
                  <td className="px-4 py-2 text-right font-mono" data-numbers>{formatCurrency(emp.taxWithheld)}</td>
                  <td className="px-4 py-2 text-right font-mono" data-numbers>{formatCurrency(emp.superContribution)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Raw payload */}
      <Card className="card-elevated">
        <div className="px-5 py-3 border-b font-semibold text-sm">Pay event payload</div>
        <CardContent>
          <pre className="text-xs overflow-auto max-h-96 bg-muted/40 rounded-md p-4">
            {JSON.stringify(payload, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
